import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';

const CompanyContext = createContext();

export const useCompanies = () => {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompanies must be used within a CompanyProvider');
  }
  return context;
};

export const CompanyProvider = ({ children }) => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/v1/company?limit=10000&return_logo=true');

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      let companiesArray = [];
      if (data.companies) {
        try {
          companiesArray = typeof data.companies === 'string'
            ? JSON.parse(data.companies)
            : data.companies;
        } catch (parseError) {
          console.error('Error parsing companies JSON:', parseError);
          companiesArray = [];
        }
      }

      if (!Array.isArray(companiesArray)) {
        companiesArray = [];
      }

      setCompanies(companiesArray);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching companies:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  // 👇 Build displayNames and logosByDisplayName here
  const companyDisplayNames = useMemo(
    
    () => companies.map(c => c.company_display_name).filter(Boolean),
    [companies]
  );

    const displayNamesByCompany = useMemo(() => {
    const map = {};
    companies.forEach(c => {
      if (c.company_name && (c.company_display_name || c.company_name)) {
        map[c.company_name] = c.company_display_name || c.company_name;
      }
    });
    return map;
  }, [companies]);

  const companyLogos = useMemo(() => {
    const map = {};
    companies.forEach(c => {
      if (c.company_name && c.company_website) {
        map[c.company_name] = `https://img.logo.dev/${c.company_website}?token=pk_M0m3xPUmQQaKLD399NpbYw`;
      }
    });
    return map;
  }, [companies]);

  const value = {
    companies,
    companyDisplayNames,
    displayNamesByCompany,
    companyLogos,
    loading,
    error,
    refetch: fetchCompanies
  };

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
};

export default CompanyContext;
